import React, { FC, memo, useCallback, useEffect, useState, Suspense, useMemo } from 'react';
import styles from './product-details.module.css';
import { useProduct } from '~/shared/utils/product/hooks/useProduct';
import {
    M130ProductDetailModule,
    M131ProductDetailModule,
    ProductViewModel,
} from '~/lib/data-contract';
import ImageGallery from './image-gallery/image-gallery.componen';
import qs from 'qs';
import { ProductTagsLegacy, Accordion, RawHtml, Typography, Usp, Modal } from '~/shared/components';
import ColorSelector from './color-selector/color-selector.component';
import { useAlgoliaAnalytics, useNotification } from '~/shared/hooks';
import { useFrame, useTranslation } from '~/shared/utils';
import { useBasketAdd } from '~/features/basket';
import Easysize from './easy-size/easy-size.component';
import Actions from './actions/actions.component';
import ReserveContainer from '~/features/Reserve/ReserveContainer';
import { addResizeVariablesToProductImage } from '~/shared/utils/product';
import Head from 'next/head';
import { PriceGroup } from '~/shared/components/price-group/price-group.component';
import { SizeSelector } from '~/shared/components/size-selector/size-selector.component';
import { Container } from '../container/container.component';

type Props = M130ProductDetailModule | M131ProductDetailModule;

const ProductDetails: FC<Props> = memo(({ sizeGuideContent }) => {
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
    const [sizeError, setSizeError] = useState<boolean>(false);
    const [isReserveActive, setIsReserveActive] = useState(false);

    const { data: product } = useProduct();
    const { trackAddToBasket } = useAlgoliaAnalytics();
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { push } = useNotification();
    const { add } = useBasketAdd();

    const {
        images,
        accordions,
        name,
        subtitle,
        priceDetails,
        colors,
        sizes,
        productColorId: productModelColorId,
        imageTags,
        sizingText,
    } = product as ProductViewModel;

    const accordionItems = useMemo(
        () =>
            accordions?.map(({ title = '', value = '' }, index) => ({
                initAsOpen: index === 0,
                id: `${title}-${index}`,
                title,
                content: <RawHtml html={value} />,
            })),
        [accordions]
    );

    const addToBasket = useCallback(() => {
        if (!selectedVariantId) {
            setSizeError(true);
            return;
        }

        if (!productModelColorId) {
            push({ severity: 'warning', text: translate('message.fatalError') });
            return;
        }

        const { hash } = window.location;
        const params = qs.parse(hash.slice(1));
        if (params.queryId) {
            trackAddToBasket([productModelColorId.toString()], params.queryId.toString());
        }
        add({
            productId: productModelColorId,
            variantId: selectedVariantId,
            quantity: 1,
            pageViewId: window.EasySizeParametersDebug?.easysize?.pageview_id || '',
            userId: window.EasySizeParametersDebug?.easysize?.user_id || '',
        });
    }, [add, productModelColorId, push, trackAddToBasket, translate, selectedVariantId]);

    const handleReserve = () => {
        if (!selectedVariantId) {
            setSizeError(true);
            return;
        }
        setIsReserveActive(true);
    };

    useEffect(() => {
        // Reset variant id on product change and track new product
        setSelectedVariantId(null);
    }, [product?.productColorId]);

    useEffect(() => {
        if (sizes?.length === 1 && !selectedVariantId) {
            setSelectedVariantId(sizes[0].variantId || null);
        }
    }, [selectedVariantId, sizes]);

    if (!product) {
        return null;
    }

    // We want to make sure the og:image is exactly the same as presented in the Structured Data, so here we go!
    const resizedFirstProductImage = addResizeVariablesToProductImage(images?.[0]?.url);

    return (
        <>
            {resizedFirstProductImage && (
                <Head>
                    <meta name="image" content={resizedFirstProductImage} />
                    <meta property="og:image" content={resizedFirstProductImage} />
                </Head>
            )}
            <Container spacingTop="none" spacingBottom="large" hasGutter="small">
                <div className={styles.root}>
                    <div className={styles.imageGallery}>
                        <div className={styles.productTagsTop}>
                            <ProductTagsLegacy
                                productTags={product?.imageTags}
                                selectFrom="top"
                                layout="spaced-list"
                            />
                        </div>
                        <ImageGallery images={images} />
                    </div>
                    <div>
                        {/* Wrapper needed for the child's sticky element to work */}

                        <div className={styles.actionsWrapper}>
                            <div className={styles.header}>
                                {name && (
                                    <Typography variant="display2" as="h1">
                                        {name}
                                    </Typography>
                                )}
                                {subtitle && (
                                    <Typography variant="body" as="div">
                                        {subtitle}
                                    </Typography>
                                )}
                                <ProductTagsLegacy
                                    productTags={imageTags}
                                    layout="list"
                                    selectFrom="bottom"
                                />
                                {priceDetails && (
                                    <PriceGroup
                                        {...priceDetails}
                                        variant="display4"
                                        imageTags={imageTags}
                                        isPDP
                                    />
                                )}
                            </div>
                            <ColorSelector colors={colors} activeColorId={productModelColorId} />
                            <SizeSelector
                                sizes={sizes}
                                inputName="pdp-size-selector"
                                selectedVariantId={selectedVariantId}
                                onSelect={(val) => {
                                    setSelectedVariantId(val);
                                    setSizeError(false);
                                }}
                                isError={sizeError}
                                sizingText={sizingText}
                            />
                            <div className={styles.sizeActions}>
                                {sizeGuideContent && <RawHtml html={sizeGuideContent} />}
                                <Easysize
                                    product={product}
                                    activeSizeId={selectedVariantId}
                                    onSizeFound={setSelectedVariantId}
                                />
                            </div>
                            <Actions
                                sizes={sizes}
                                activeSizeId={selectedVariantId}
                                productColorId={productModelColorId}
                                onAddToBasket={addToBasket}
                                onReserve={handleReserve}
                            />

                            {frame?.usp?.uspList?.length ? (
                                <ul className={styles.uspList}>
                                    {frame.usp.uspList?.map((uspItem, index) => (
                                        <li key={index}>
                                            <Usp
                                                uspItem={uspItem}
                                                shade="paragraph"
                                                textDecoration="underline"
                                            />
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                        <div className={styles.stickyContent}>
                            <Accordion multiple items={accordionItems} />
                        </div>
                    </div>
                </div>
            </Container>
            <Suspense>
                <Modal
                    headline={translate('reservation.overlayHeading')}
                    isOpen={isReserveActive}
                    onClose={() => setIsReserveActive(false)}
                    variant="compact"
                    scrollLock={true}
                    doNotRemoveContentFromDOMWhenClosed
                >
                    <ReserveContainer
                        key={productModelColorId}
                        initialVariantId={selectedVariantId}
                        product={product}
                        setIsReserveActive={setIsReserveActive}
                    />
                </Modal>
            </Suspense>
        </>
    );
});

ProductDetails.displayName = 'ProductDetails';
export default ProductDetails;
